//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PojoMap } from 'pojo-maps';
import { DateUtils } from 'unison';

import * as storyAnalyticsApi from '@apis/gateway-instagram/instagramStoryAnalyticsApi';
import {
  IInstagramStoryGroupAggregateStats,
  IInstagramStoryLinkData,
  InstagramStorySearchSortKey,
} from '@apis/gateway-instagram/instagramStoryAnalyticsApiModel';
import {
  IInstagramAccountStoryGroupsEngagementState,
  INSTAGRAM_ACCOUNT_STORY_GROUPS_PAGE_SIZE,
  ValidStoryGroupSortKeys,
} from '@instagram-account/redux/engagement/story-groups/instagramAccountStoryGroupsEngagementModel';
import Cuid from '@models/Cuid';
import DateString from '@models/DateString';
import {
  IInstagramStoryClip,
  IInstagramStoryGroup,
} from '@models/IInstagramMediaItem';
import TimeZoneId from '@models/TimeZoneId';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { UniversalSearchSortDirection } from '@models/UniversalSearchSortDirection';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import normalize, { Normalized } from '@utils/normalize';

const initialState: IInstagramAccountStoryGroupsEngagementState = {
  flags: {
    isFetchingStoryGroups: false,
    isFetchingStoryClips: false,
  },
  data: {
    storyGroups: Normalized.empty(),
    storyClips: PojoMap.empty(),
  },
  ui: {
    dateRange: DateUtils.getDateRangeFromAlias('last7Days'),
    storyGroupsSortKey: InstagramStorySearchSortKey.Date,
    storyGroupsSortDirection: UniversalSearchSortDirection.Desc,
    storyGroupsPageOffset: 0,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramAccountStoryGroupsEnagagement,
  initialState,
  reducers: {
    requestStoryGroups(state) {
      state.flags.isFetchingStoryGroups = true;
      state.data.storyGroups = Normalized.empty();
      state.data.aggregateStoryStats = undefined;
      state.data.aggregateStoryComparisonStats = undefined;
    },
    receiveStoryGroupsSucceeded(
      state,
      action: PayloadAction<{
        storyGroups: Normalized<IInstagramStoryGroup, DateString>;
        linkData: IInstagramStoryLinkData[];
        linkDataByDateString: PojoMap<DateString, IInstagramStoryLinkData[]>;
        aggregateStats: IInstagramStoryGroupAggregateStats;
        aggregateComparisonStats: IInstagramStoryGroupAggregateStats;
      }>,
    ) {
      state.flags.isFetchingStoryGroups = false;
      state.data.storyGroups = action.payload.storyGroups;
      state.data.linkData = action.payload.linkData;
      state.data.linkDataByDateString = action.payload.linkDataByDateString;
      state.data.aggregateStoryStats = action.payload.aggregateStats;
      state.data.aggregateStoryComparisonStats =
        action.payload.aggregateComparisonStats;
    },
    receiveStoryGroupsFailed(state) {
      state.flags.isFetchingStoryGroups = false;
    },
    requestStoryClips(state) {
      state.flags.isFetchingStoryClips = true;
    },
    receiveStoryClipsSucceeded(
      state,
      action: PayloadAction<PojoMap<Cuid, IInstagramStoryClip>>,
    ) {
      state.flags.isFetchingStoryClips = false;
      state.data.storyClips = action.payload;
    },
    receiveStoryClipsFailed(state) {
      state.flags.isFetchingStoryClips = false;
    },
    setStoryGroupsSortKey(
      state,
      action: PayloadAction<ValidStoryGroupSortKeys>,
    ) {
      state.ui.storyGroupsSortKey = action.payload;
    },
    setStoryGroupsSortDirection(
      state,
      action: PayloadAction<UniversalSearchSortDirection>,
    ) {
      state.ui.storyGroupsSortDirection = action.payload;
    },
    setStoryGroupsDateRange(state, action: PayloadAction<IUnisonDateRange>) {
      state.ui.dateRange = action.payload;
    },
    setStoryGroupsPage(state, action: PayloadAction<number>) {
      state.ui.storyGroupsPageOffset =
        action.payload * INSTAGRAM_ACCOUNT_STORY_GROUPS_PAGE_SIZE;
    },
  },
});

export const {
  setStoryGroupsDateRange,
  setStoryGroupsPage,
  setStoryGroupsSortDirection,
  setStoryGroupsSortKey,
} = actions;

export const fetchStoryGroupsData = createThunk(
  (targetDate?: DateString) => async (dispatch, getState) => {
    dispatch(actions.requestStoryGroups());

    const { dateRange } = getState().instagram.engagement.storyGroups.ui;
    const { endDate, startDate } = targetDate
      ? { startDate: targetDate, endDate: targetDate }
      : DateString.fromUnisonDateRange(dateRange);

    const params = {
      startDate,
      endDate,
    } as const;

    try {
      const res = await storyAnalyticsApi.getStoryGroupsAnalytics(params);
      const data = {
        storyGroups: normalize(res.storyGroups, (sg) =>
          DateString.fromTimestamp(sg.startTime, TimeZoneId.AmericaLosAngeles),
        ),
        linkData: res.swipeUpLinkData,
        linkDataByDateString: res.swipeUpLinkDataByLocalDate,
        aggregateStats: res.aggregateStats,
        aggregateComparisonStats: res.aggregateComparisonStats,
      };
      dispatch(actions.receiveStoryGroupsSucceeded(data));
    } catch (e) {
      dispatch(actions.receiveStoryGroupsFailed());
    }
  },
);

export const fetchStoryClips = createThunk(
  (targetDate: DateString) => async (dispatch, getState) => {
    dispatch(actions.requestStoryClips());
    const targetGroup = await (async () => {
      let group = Normalized.getById(
        getState().instagram.engagement.storyGroups.data.storyGroups,
        targetDate,
      );
      if (!group) {
        await dispatch(fetchStoryGroupsData(targetDate));
        group = Normalized.getById(
          getState().instagram.engagement.storyGroups.data.storyGroups,
          targetDate,
        );
      }
      return group;
    })();
    try {
      if (targetGroup) {
        const clips = await storyAnalyticsApi.getStoryClipsAnalytics(
          targetGroup.storyGroupClips.map((c) => c.cuid),
        );

        dispatch(actions.receiveStoryClipsSucceeded(clips));
      }
    } catch (e) {
      dispatch(actions.receiveStoryClipsFailed());
    }
  },
);

export const getStoryGroupsDataDownload = createThunk(
  () => async (dispatch, getState) => {
    const {
      dateRange,
      storyGroupsSortDirection: sortDirection,
      storyGroupsSortKey: sortKey,
    } = getState().instagram.engagement.storyGroups.ui;

    const params = {
      ...DateString.fromUnisonDateRange(dateRange),
      sortDirection,
      sortKey,
    } as const;

    return storyAnalyticsApi.getStoryGroupsDataDownload(params);
  },
);

export default reducer;
